/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { getLoanNumber } from '../../storage/userStorage';

type UseLoanData = {
  loanData: UnknownLoanDatas;
  loadingLoanData: boolean;
  refetchingLoanData: boolean;
  updateLoanData: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  reloadLoanData: () => void;
  refetch: (options: { throwOnError: boolean; cancelRefetch: boolean }) => any;
};

const useLoanData = (pathNameOrLoanNumber = ''): UseLoanData => {
  const { get } = useAbortController();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const [searchParams] = useSearchParams();
  const loanNumber = searchParams.get('loanNumber') || getLoanNumber();
  const path = useMemo(() => {
    if (!pathNameOrLoanNumber && !loanNumber) {
      return '';
    }
    if (!pathNameOrLoanNumber) {
      return `/${loanNumber}`;
    }
    return '/' + pathNameOrLoanNumber;
  }, [pathNameOrLoanNumber, loanNumber]);
  const queryKey = useMemo(() => ['loanDatas', path, token], [path, token]);

  // Fetch communication data using react-query
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!path || !token) return null;
      try {
        const request = {
          url: `live/loan${path}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanData.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useLoanData.caught: ${error.message}`);
        // Simulated fetch call for testing, TODO: Remove before public release.
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanData = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const updateLoanData = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan${path}`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanData.response: `, response);
      reloadLoanData();
      return response;
    },
    [path, get, reloadLoanData, token],
  );

  return {
    loanData: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanData: isLoading,
    refetchingLoanData: isFetching,
    updateLoanData,
    reloadLoanData,
    refetch,
  };
};

export default useLoanData;
